/**
 * UUIDv7 in the browser — the counterpart of `api/src/lib/ids.ts`.
 *
 * The client chooses the id, and that is what makes a queued create idempotent:
 * the server upserts by id, so the same mutation sent twice is one row. It also
 * means a row exists — and can be shown, edited and linked to — before anything
 * has reached the server.
 *
 * Time-ordered, so ids sort by creation and index locality is good at both ends.
 */
export function uuidv7(now: number = Date.now()): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // 48-bit big-endian millisecond timestamp. `now` exceeds 32 bits, so it is
  // written in two halves rather than with a single shift — `<<` in JavaScript
  // operates on 32-bit integers and would silently lose the top bits.
  const high = Math.floor(now / 0x1_0000_0000);
  const low = now % 0x1_0000_0000;
  bytes[0] = (high >>> 8) & 0xff;
  bytes[1] = high & 0xff;
  bytes[2] = (low >>> 24) & 0xff;
  bytes[3] = (low >>> 16) & 0xff;
  bytes[4] = (low >>> 8) & 0xff;
  bytes[5] = low & 0xff;

  // Version 7 in the high nibble of byte 6.
  bytes[6] = (bytes[6]! & 0x0f) | 0x70;
  // RFC 4122 variant in the top two bits of byte 8.
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  const group = (from: number, to?: number) => hex.slice(from, to);
  return [group(0, 8), group(8, 12), group(12, 16), group(16, 20), group(20)].join("-");
}
